export type Routes = {
  login: string;
  recoverPassword: string;
  resetPassword: string;
  multiFactorAuth: string;
  impersonate: string;
  loginTenant: string;
  resetPasswordWithCode: (userId: number, resetCode: string, tenantId: number) => string;
  iframeCampaignsList: string;
  activateEmail: string;
  register: string;
  jobBoards: string;
  newJobBoard: string;
  jobBoard: (id?: string | number) => string;
  atsConnections: string;
  newAtsConnection: string;
  atsConnection: (id?: string | number) => string;
  users: string;
  newUser: string;
  userMe: string;
  home: string;
  user: (id?: string | number) => string;
  search: (params?: { campaignId?: number | string; tab?: string; justCreatedNewCampaign?: boolean }) => string;
  sharedCandidatePage: (params?: {
    candidateId?: number | string;
    campaignId?: number | string;
    userId?: number | string;
  }) => string;
  candidatePage: (params?: {
    candidateId?: number | string;
    campaignId?: number | string;
    userId?: number | string;
  }) => string;
  browserExtension: string;
  notifications: string;
  teams: string;
  newTeam: string;
  team: (id?: string | number) => string;
  detailsTeam: (id?: string | number) => string;
  tenantEmailSettings: string;
  gtNotAvailable: string;
  rjCommHubPublicVacancyDetails: string;
  rjCommHubCandidateInterested: string;
  rjCommHubCandidateNotInterested: string;
  rjCommHubCandidateConfirmation: (status?: string) => string;
  rjCommHubQuestionnaireConfirmation: string;
  rjCommHubJobAlertsConfirmation: string;
  optOutCommunication: string;
  interviewFeedbackCandidate: string;
  interviewFeedbackRecruiter: string;
  interviewScheduling: (token?: string) => string;
  /** Public candidate-facing reschedule of an existing booking. The token is the only authorization. */
  bookingReschedule: (token?: string) => string;
  /** The same screen in the tenant's own chrome — this is the URL in the interview-booked email. */
  screeningReschedule: (token?: string) => string;
  robinWrapped: string;
  dashboard: string;
  emailSettings: string;
  nylasSuccess: string;
  nylasFailed: string;
  ratedCandidates: string;
  centralInbox: string;
  questionnaire: string;
  /** Public candidate-facing screening questionnaire. The token is the only authorization. */
  screening: (token?: string) => string;
  /**
   * The hop the AI-interview e-mail links to instead of the vendor's own URL. It exists so the
   * token is written to storage on our origin before the candidate leaves, because the redirect
   * they come back on carries none.
   */
  screeningAiInterview: (token?: string) => string;
  /**
   * Process feedback as a screen of its own, which is what the star row in an intake e-mail links
   * to. It takes a `?rating=` the page saves on arrival, and is the one feedback surface where the
   * row is always identified, because the token is in the URL.
   */
  screeningFeedback: (token?: string) => string;
  /**
   * Where the AI-interview vendor drops the candidate. Static, and tokenless — the redirect
   * carries no identity, which is the whole reason this screen recalls one from storage.
   */
  intakeInterviewComplete: string;
  jobRecommendations: string;
  jobNotificationsEnabled: string;
  jobRecommendationsPaused: string;
  jobRecommendationDetails: string;
  jobRecommendationNotificationDetail: string;
  jobRecommendationNotificationList: string;
  candidateProfile: string;
  notFound: string;
  /////////////////////////////
  /// Admin routes
  /////////////////////////////
  admin: {
    root: string;
    dashboard: string;
    /** Staff-only Indeed outreach monitor. Its views and filters live in the query string. */
    outreach: string;
  };
  /////////////////////////////
  /// External routes
  /////////////////////////////
  helpCenter: string;

  robinJr: {
    home: string;
    chat: (id?: string | number) => string;
  };
  oauth2: string;
};

export const routes: Routes = {
  login: '/',
  home: '/home',
  recoverPassword: '/Account/ForgotPassword',
  resetPassword: '/Account/ResetPassword',
  multiFactorAuth: '/Account/MultiFactorAuth',
  impersonate: '/Account/ImpersonateSignIn',
  loginTenant: '/Account/LoginTenant',
  resetPasswordWithCode: (userId, resetCode, tenantId) =>
    `/Account/ResetPassword?userId=${userId}&resetCode=${resetCode}&tenantId=${tenantId}`,
  iframeCampaignsList: '/search/campaigns',
  userMe: '/Users/User/Me/',
  user: (id = ':id') => `/Users/User/${id}`,
  activateEmail: '/Account/EmailActivation',
  register: '/TenantRegistration/Register',
  jobBoards: '/Customer/JobBoards',
  newJobBoard: '/Customer/JobBoard',
  jobBoard: (id = ':id') => `/Customer/JobBoard/${id}`,
  atsConnections: '/Customer/ATS',
  newAtsConnection: '/Customer/ATS/ATS',
  atsConnection: (id = ':id') => `/Customer/ATS/ATS/${id}`,
  users: '/Customer/Users',
  newUser: '/Customer/Users/User/',
  search: (props) => {
    const { campaignId, justCreatedNewCampaign, tab } = props || {};
    const baseUrl = '/search';
    const pathUrl = [];
    const queryUrl = [];

    if (campaignId !== undefined) pathUrl.push(campaignId.toString());
    if (tab !== undefined) pathUrl.push(tab);

    if (justCreatedNewCampaign !== undefined)
      queryUrl.push(['justCreatedNewCampaign', justCreatedNewCampaign ? 'true' : 'false']);

    return (
      baseUrl +
      pathUrl.reduce((str, value) => str + `/${value}`, '') +
      queryUrl.reduce((str, [key, value], index) => (index === 0 ? `?${key}=${value}` : str + `&${key}=${value}`), '')
    );
  },
  sharedCandidatePage: ({ campaignId = ':campaignId', candidateId = ':candidateId', userId = ':userId' } = {}) =>
    `/shared/u/${userId}/s/${campaignId}/c/${candidateId}`,
  candidatePage: ({ campaignId = ':campaignId', candidateId = ':candidateId', userId = ':userId' } = {}) =>
    `/candidate/u/${userId}/s/${campaignId}/c/${candidateId}`,
  browserExtension: '/BrowserExtension',
  notifications: '/notifications',
  teams: '/teams',
  newTeam: '/teams/team',
  team: (id = ':id') => `/teams/team/${id}`,
  detailsTeam: (id = ':id') => `/teams/team/details/${id}`,
  tenantEmailSettings: '/company_email_settings',
  robinWrapped: '/wrapped',
  dashboard: '/dashboard',
  emailSettings: '/email-settings',
  nylasSuccess: '/nylas/success',
  nylasFailed: '/nylas/failed',
  ratedCandidates: '/ratedCandidates',
  centralInbox: '/central-inbox',
  questionnaire: '/questionnaire',
  // Declared after `screeningReschedule` in the router, but order here is irrelevant — React Router
  // ranks the static `reschedule` segment above this one's `:token`.
  screening: (token = ':token') => `/screening/${token}`,
  // The static tail outranks `screening`'s bare `:token`, so this cannot be shadowed by it. It
  // does not collide with `screeningReschedule` either: that one's static `reschedule` segment
  // wins on the only path that reaches both.
  screeningAiInterview: (token = ':token') => `/screening/${token}/ai-interview`,
  // Same ranking as its sibling above, for the same reason.
  screeningFeedback: (token = ':token') => `/screening/${token}/feedback`,
  // Kept in the `/screening/` namespace the rest of the intake journey already occupies, so
  // the candidate does not cross into an unrelated-looking path at the end of it. React
  // Router ranks this static segment above `screening`'s `:token` regardless of order.
  intakeInterviewComplete: '/screening/interview-complete',
  jobRecommendations: '/rj-comm-hub/not-interested',
  jobNotificationsEnabled: '/job-recommendation/notifications-enabled',
  jobRecommendationsPaused: '/job-recommendation/paused',
  jobRecommendationDetails: '/job-recommendation/details',
  jobRecommendationNotificationDetail: '/rj-comm-hub/job-recommendation-detail',
  jobRecommendationNotificationList: '/rj-comm-hub/job-recommendations-list',
  candidateProfile: '/candidate/profile',
  /////////////////////////////
  /// Admin routes
  /////////////////////////////
  admin: {
    root: '/admin',
    dashboard: '/admin/dashboard',
    outreach: '/admin/outreach',
  },
  /////////////////////////////
  /// External routes
  /////////////////////////////
  helpCenter: 'https://intercom.help/recruit-robin',
  gtNotAvailable: '/google-translate-not-available',
  rjCommHubPublicVacancyDetails: '/rj-comm-hub/vacancy',
  rjCommHubCandidateInterested: '/rj-comm-hub/interested',
  rjCommHubCandidateNotInterested: '/rj-comm-hub/not-interested',
  rjCommHubCandidateConfirmation: (status = ':status') => `/rj-comm-hub/confirmation/${status}`,
  rjCommHubQuestionnaireConfirmation: '/rj-comm-hub/questionnaire-confirmation',
  rjCommHubJobAlertsConfirmation: '/rj-comm-hub/job-alerts-confirmation',
  optOutCommunication: '/candidate/opt-out-communication',
  interviewFeedbackCandidate: '/rj-comm-hub/interview-feedback',
  interviewFeedbackRecruiter: '/interview-feedback',
  interviewScheduling: (token = ':token') => `/interview/schedule/${token}`,
  bookingReschedule: (token = ':token') => `/booking/reschedule/${token}`,
  screeningReschedule: (token = ':token') => `/screening/reschedule/${token}`,
  notFound: '/not_found',
  oauth2: '/oauth2/*',

  robinJr: {
    home: '/robin-jr',
    chat: (id: string | number = ':id') => `/robin-jr/chat/${id.toString()}`,
  },
};
